import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { dashboardApi, integrationsApi } from '../services/api';
import './Dashboard.css';

interface RevenueByService {
  service_type: string;
  monthly_total: string;
  mappings: number;
  products: number;
}

interface SyncHealth {
  integration: string;
  label: string;
  configured: boolean;
  nightly_enabled: boolean;
  last_status: string | null;
  last_sync: string | null;
  last_trigger: string | null;
  last_items: number | null;
  last_message: string | null;
}

interface DashboardData {
  grand_total: string;
  ready_total: string;
  revenue_by_service: RevenueByService[];
  total_customers: number;
  ready_to_bill: number;
  incomplete: number;
  unmapped: number;
  customers_needing_pricing: number;
  unpriced_products: number;
  total_products: number;
  total_m365_licenses: number;
  m365_mismatches: number;
  m365_matches: number;
  m365_mismatch_examples: {
    customer_id: number; customer_name: string;
    m365_total: number; cloudblue_total: number; mismatch_lines: number;
  }[];
  sync_health: SyncHealth[];
  recent_sync_logs: {
    id: number; integration: string; trigger: string; status: string;
    items_synced: number | null; started_at: string | null;
    duration_seconds: number | null; message: string | null;
  }[];
  top_customers: {
    customer_id: number; customer_name: string; monthly_total: string;
    service_count: number; products: number;
  }[];
}

const ZAR = (v: string | number) =>
  `R ${(typeof v === 'string' ? parseFloat(v) || 0 : v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ZAR0 = (v: string | number) =>
  `R ${Math.round(typeof v === 'string' ? parseFloat(v) || 0 : v).toLocaleString('en-ZA')}`;

const SERVICE_LABELS: Record<string, string> = {
  cloudblue: 'CloudBlue (M365 CSP)',
  cove: 'Cove Backup',
  proxmox: 'Proxmox Hosting',
  whmcs: 'WHMCS',
  xero: 'Xero',
  sentinelone: 'SentinelOne',
  manual: 'Manual services',
};

const SERVICE_COLORS: Record<string, string> = {
  cloudblue: '#6366F1',
  cove: '#10B981',
  proxmox: '#F59E0B',
  whmcs: '#EF4444',
  xero: '#13B5EA',
  sentinelone: '#8B5CF6',
  manual: '#64748B',
};

const SYNC_ICON: Record<string, string> = {
  success: '✅', partial: '⚠️', error: '❌', running: '⏳',
};

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await dashboardApi.get());
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSyncAll = async () => {
    try {
      await integrationsApi.syncAll();
      toast.success('Full sync started — see the Integrations page for progress');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to start sync');
    }
  };

  if (loading || !data) {
    return <div className="dashboard-page"><div className="loading">Loading dashboard…</div></div>;
  }

  const maxRevenue = Math.max(...data.revenue_by_service.map(r => parseFloat(r.monthly_total) || 0), 1);
  const healthProblems = data.sync_health.filter(
    s => s.configured && (s.last_status === 'error' || !s.last_sync));

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1>📊 Dashboard</h1>
          <p className="page-subtitle">Billing command center · {new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="dash-header-actions">
          <button className="dash-btn" onClick={handleSyncAll}>🔄 Sync all</button>
          <Link to="/billing" className="dash-btn primary">💳 Invoice Prep →</Link>
        </div>
      </div>

      {/* Hero money cards */}
      <div className="dash-hero">
        <div className="hero-card main">
          <div className="hero-label">Monthly run rate</div>
          <div className="hero-value">{ZAR0(data.grand_total)}</div>
          <div className="hero-sub">{ZAR(data.grand_total)} across {data.total_products.toLocaleString()} billable items</div>
        </div>
        <div className="hero-card">
          <div className="hero-label">Ready to invoice</div>
          <div className="hero-value">{ZAR0(data.ready_total)}</div>
          <div className="hero-sub">{data.ready_to_bill} customers ready</div>
        </div>
        <div className="hero-card clickable" onClick={() => navigate('/billing')}>
          <div className="hero-label">Needs pricing</div>
          <div className={`hero-value ${data.customers_needing_pricing > 0 ? 'warn' : 'ok'}`}>
            {data.customers_needing_pricing}
          </div>
          <div className="hero-sub">{data.unpriced_products} unpriced products</div>
        </div>
        <div className="hero-card clickable" onClick={() => navigate('/billing')}>
          <div className="hero-label">M365 ≠ CloudBlue</div>
          <div className={`hero-value ${data.m365_mismatches > 0 ? 'bad' : 'ok'}`}>
            {data.m365_mismatches}
          </div>
          <div className="hero-sub">{data.m365_matches} match · {data.total_m365_licenses.toLocaleString()} licenses</div>
        </div>
      </div>

      <div className="dash-columns">
        <div className="dash-col">
          {/* Revenue by service */}
          <div className="dash-panel">
            <div className="panel-head">
              <h3>Revenue by service <span className="panel-sub">/month equivalent</span></h3>
            </div>
            <div className="revenue-bars">
              {data.revenue_by_service
                .slice()
                .sort((a, b) => parseFloat(b.monthly_total) - parseFloat(a.monthly_total))
                .map(r => {
                  const val = parseFloat(r.monthly_total) || 0;
                  return (
                    <div key={r.service_type} className="revenue-row">
                      <div className="revenue-meta">
                        <span className="revenue-name">
                          <span className="revenue-dot" style={{ background: SERVICE_COLORS[r.service_type] || '#999' }} />
                          {SERVICE_LABELS[r.service_type] || r.service_type}
                        </span>
                        <span className="revenue-val">{ZAR0(val)}</span>
                      </div>
                      <div className="revenue-track">
                        <div className="revenue-fill"
                             style={{ width: `${Math.max(2, (val / maxRevenue) * 100)}%`,
                                      background: SERVICE_COLORS[r.service_type] || '#999' }} />
                      </div>
                      <div className="revenue-counts">{r.mappings} mappings · {r.products} products</div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Customer readiness */}
          <div className="dash-panel">
            <div className="panel-head"><h3>Customer readiness</h3></div>
            <div className="readiness-strip">
              <div className="ready-chip ok" onClick={() => navigate('/billing')}>
                <strong>{data.ready_to_bill}</strong> ready
              </div>
              <div className="ready-chip warn" onClick={() => navigate('/billing')}>
                <strong>{data.incomplete}</strong> incomplete
              </div>
              <div className="ready-chip bad" onClick={() => navigate('/billing')}>
                <strong>{data.unmapped}</strong> unmapped
              </div>
              <div className="ready-chip neutral">
                <strong>{data.total_customers}</strong> total
              </div>
            </div>
            {data.m365_mismatch_examples.length > 0 && (
              <div className="mismatch-list">
                <div className="mismatch-title">Biggest M365 ↔ CloudBlue mismatches</div>
                {data.m365_mismatch_examples.map(m => (
                  <div key={m.customer_id} className="mismatch-row" onClick={() => navigate('/billing')}>
                    <span className="mismatch-name">{m.customer_name}</span>
                    <span className="mismatch-detail">
                      M365 {m.m365_total} vs CB {m.cloudblue_total} · {m.mismatch_lines} line{m.mismatch_lines !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="dash-col">
          {/* Sync health */}
          <div className="dash-panel">
            <div className="panel-head">
              <h3>Sync health</h3>
              <Link to="/integrations" className="panel-link">Manage →</Link>
            </div>
            {healthProblems.length > 0 && (
              <div className="sync-alert">
                {healthProblems.length} integration{healthProblems.length !== 1 ? 's' : ''} need attention
              </div>
            )}
            <div className="sync-list">
              {data.sync_health.map(s => (
                <div key={s.integration} className={`sync-row ${!s.configured ? 'dim' : ''}`}>
                  <span className={`sync-dot ${s.configured ? (s.last_status || 'none') : 'off'}`} />
                  <span className="sync-name">{s.label}</span>
                  <span className="sync-info">
                    {!s.configured ? 'not configured'
                      : s.last_sync ? `${SYNC_ICON[s.last_status || ''] || ''} ${timeAgo(s.last_sync)}${s.last_items != null ? ` · ${s.last_items} items` : ''}`
                      : 'never synced'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Top customers */}
          <div className="dash-panel">
            <div className="panel-head">
              <h3>Top customers <span className="panel-sub">by monthly run rate</span></h3>
            </div>
            <table className="top-customers">
              <tbody>
                {data.top_customers.map((c, i) => (
                  <tr key={c.customer_id} onClick={() => navigate('/billing')}>
                    <td className="rank">{i + 1}</td>
                    <td className="name">{c.customer_name}
                      <span className="top-sub">{c.products} items · {c.service_count} service{c.service_count !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="amount">{ZAR0(c.monthly_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent syncs */}
          <div className="dash-panel">
            <div className="panel-head"><h3>Recent sync activity</h3></div>
            <div className="recent-logs">
              {data.recent_sync_logs.length === 0 ? (
                <div className="recent-empty">No syncs recorded yet — run one from the Integrations page.</div>
              ) : data.recent_sync_logs.map(l => (
                <div key={l.id} className="recent-row">
                  <span className="recent-icon">{SYNC_ICON[l.status] || '·'}</span>
                  <span className="recent-name">{l.integration}</span>
                  <span className="recent-msg">{l.message}</span>
                  <span className="recent-when">{timeAgo(l.started_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
